/* eslint-disable max-lines */
// deps
import React, { memo, useCallback, useRef, useContext, useEffect } from "react";
import { withStyles, format } from "@swiggy-private/core-ui";
import { captureError } from "@swiggy-private/core-ui";
import CommonUIWrapper from "@cart-client/components/common-ui-wrapper/CommonUIWrapper";
import styles from "./CartUIWrapper.scss";
import {
    getBridgeSdk,
    GlobalHeaderState,
    SwgyHeaderKeysList,
    closeWebview,
    CheckoutContext,
    CheckoutActionTypes,
    useCustomHardwareBack,
    CheckoutRoute,
    GroupOrderCartContext,
    isPopFlow,
    BackNavigationContext,
    AnalyticsContext,
    getNativeChannelDeepLink,
    isJioHotstarFlow,
} from "@swiggy-private/checkout-core";
import { NavigationTypes } from "@swiggy-private/native-bridge";
import { CART_STATUS_CODES } from "@swiggy-private/cart-ui";
import { getCartFeedbackFlag, updateCartFeedbackFlag } from "@cart-client/helpers/CartFeedback";
import { CART_FEEDBACK_DEEPLINK, EMPTY_LOCAL_CART_MESSAGE } from "@cart-client/constants/index";
import { getFeedbackUrl } from "@cart-client/helpers/Cart";
import { CartUIWrapperProps } from "./types";
import { CartHeaderContext } from "@cart-client/contexts/CartHeaderContext";
import { useCartHeaderLottieRenderer } from "@cart-client/hooks/useCartHeaderLottie";
import { useCommonCartHeaderLottie } from "@cart-client/hooks/useCommonCartheaderLottie";
import CartHeaderContentContainer from "../cart-header-content/CartHeaderContentContainer";
import { CartHeaderLottieContext } from "@cart-client/contexts/CartHeaderLottieContext";
import { clickMenuBackDynamic } from "@cart-client/analytics/events/CommonHeader";
import { CartContext } from "@cart-client/contexts/CartContext";
import { isEmpty } from "@swiggy-private/js-utils";
import { CUSTOM_BACK_HANDLING_ROUTES } from "./constants";

export interface ScrollableDivElement extends HTMLDivElement {
    getScrollPosition: () => number;
}

function CartUIWrapper({ children, ...props }: CartUIWrapperProps): React.ReactElement {
    const {
        containerId,
        containerClass,
        headerClass,
        isPageScrolled,
        showCartFeedback,
        cartId,
        statusCode,
        showAddressWidgetTop,
        cartIncognitoModeXp,
        isIncognitoCart,
        isSavingsBannerExist,
        selectedAddress,
        isIrctcCart,
        coreLayoutCustomClassName,
        onBack,
        useHeaderV2,
    } = props;
    const { setCheckoutState } = useContext(CheckoutContext);
    const { isGroupCartFlow, state: groupCartData } = useContext(GroupOrderCartContext);
    const { cartState } = useContext(CartContext);
    const { backAction, setBackAction } = useContext(BackNavigationContext);
    const { clickEvent } = useContext(AnalyticsContext);

    const cartHeaderLottieContextValue = useCommonCartHeaderLottie();
    const cartHeaderContextValue = useCartHeaderLottieRenderer();

    const isFromBottomBar = GlobalHeaderState.getHeader(SwgyHeaderKeysList.isFromBottomBar) === "true";
    const openCartFeedbackWebview = useCallback(() => {
        const url = getFeedbackUrl();
        const cartFeedbackDeeplink = getNativeChannelDeepLink(CART_FEEDBACK_DEEPLINK);

        getBridgeSdk()
            .navigateToScreen({
                type: NavigationTypes.DEEPLINK,
                link: format(cartFeedbackDeeplink, encodeURIComponent(format(url, encodeURIComponent(cartId ?? "")))),
            })
            .catch(() => void 0);
    }, [cartId]);

    const onCloseCartWebview = useCallback(async () => {
        const isCartFeedbackShown = await getCartFeedbackFlag();
        // if XP value is true, cart feedback storage flag is false and cart status code is success (we do not need to include unserviceable cart scenarios)
        if (showCartFeedback && !isCartFeedbackShown && statusCode === CART_STATUS_CODES.SUCCESS) {
            openCartFeedbackWebview();

            // updating cart feedback storage key
            updateCartFeedbackFlag();
            return;
        }
        closeWebview();
    }, [openCartFeedbackWebview, showCartFeedback, statusCode]);

    /**
     * Generic fallback navigation handler for back button actions
     * Handles different cart flow scenarios when navigation fails or no backAction exists
     */
    const handleDefaultBackBehaviour = useCallback(() => {
        if (isGroupCartFlow) {
            getBridgeSdk()
                .getLocalCart({
                    tenant: { marketplaceBusinessline: "Regular", marketplaceCategory: "Food", marketplaceId: 1 },
                })
                .then((localCart) => {
                    if (!localCart) {
                        throw new Error(EMPTY_LOCAL_CART_MESSAGE);
                    }
                    setCheckoutState({
                        type: CheckoutActionTypes.UNLOCK_GROUP_CART,
                        payload: { nativeLocalMergedCart: localCart },
                    });
                })
                .catch(() => void 0);
        } else if (isFromBottomBar) {
            closeWebview({ params: { goToHome: "true" } });
        } else {
            onCloseCartWebview();
        }
    }, [isGroupCartFlow, isFromBottomBar, onCloseCartWebview, setCheckoutState]);

    const _onBack = useCallback(() => {
        if (!isEmpty(backAction) && backAction.deeplink && backAction.screenName) {
            getBridgeSdk()
                .navigateToScreen({
                    type: NavigationTypes.DEEPLINK,
                    link: backAction.deeplink ?? "",
                    params: {
                        shouldCloseWebview: true,
                    },
                })
                .then(() => {
                    setBackAction({ deeplink: null, screenName: null });
                })
                .catch((error) => {
                    captureError(error);
                    setBackAction({ deeplink: null, screenName: null });
                    handleDefaultBackBehaviour();
                });
            clickMenuBackDynamic(
                clickEvent,
                {
                    cartId: cartState?.localCart?.cart_id?.toString(),
                    deeplink: backAction.deeplink,
                    screenName: backAction.screenName,
                },
                groupCartData?.data?.group_cart_id,
            );
            return;
        }

        handleDefaultBackBehaviour();
    }, [backAction, setBackAction, handleDefaultBackBehaviour, clickEvent, groupCartData, cartState]);

    // Back handler: use prop override when provided; fall back to internal logic
    const backHandler = useCallback(() => {
        if (typeof onBack === "function") {
            onBack();
            return;
        }
        _onBack();
    }, [onBack, _onBack]);

    useCustomHardwareBack(CUSTOM_BACK_HANDLING_ROUTES, backHandler);

    useEffect(() => {
        if (!isEmpty(backAction) && backAction.deeplink && backAction.screenName) {
            // Push a history state to enable popstate event on hardware back
            history.pushState({ name: CheckoutRoute.CART, data: null }, "", window.location.href);
        }
    }, [backAction]); // Re-run when backAction changes

    const containerRef = useRef<ScrollableDivElement>(null);

    const isCartError = statusCode !== CART_STATUS_CODES.SUCCESS;

    return (
        <CartHeaderLottieContext.Provider value={cartHeaderLottieContextValue}>
            <CartHeaderContext.Provider value={cartHeaderContextValue}>
                <CommonUIWrapper
                    onBack={backHandler}
                    header={<CartHeaderContentContainer {...props} />}
                    content={children}
                    key={0}
                    ref={containerRef}
                    containerId={containerId}
                    containerCSSClass={containerClass}
                    headerCSSClass={headerClass}
                    isPageScrolled={isPageScrolled}
                    showAddressWidgetTop={showAddressWidgetTop}
                    cartIncognitoModeXp={cartIncognitoModeXp}
                    isIncognitoCart={isIncognitoCart}
                    isCartContentHeader={true}
                    isIrctcCart={isIrctcCart}
                    isSavingsBannerExist={isSavingsBannerExist}
                    isAddressSelected={!!selectedAddress}
                    isPopCart={isPopFlow()}
                    coreLayoutCustomClassName={coreLayoutCustomClassName}
                    isJioHotstarFlow={isJioHotstarFlow()}
                    isCartError={isCartError}
                    useHeaderV2={useHeaderV2}
                />
            </CartHeaderContext.Provider>
        </CartHeaderLottieContext.Provider>
    );
}

export default memo(withStyles(CartUIWrapper, styles));
